import {AddIcon, MinusIcon} from 'native-base';
import {Box, HBox} from '@buncha/components/core/view';
import React, {useCallback, useEffect, useState} from 'react';
import {
  useAddBagsUsedForOrder,
  useNavigateVanLoadContext,
} from '@buncha/batchDetails/hooks/NavigateVanLoad';
import {useColorThemeType, useFontTheme} from '@buncha/appStyles/hooks/theme';

import {Analytics} from '@buncha/config/analytics/analytics';
import {BottomButton} from '@buncha/components/composites/Button';
import BottomSheet from '@buncha/components/composites/bottomSheet';
import {BottomSheetStyle} from './style';
import {CustomImage} from '@buncha/components/core/Image';
import CustomerOrderInfo from '../../CustomerOrderInfo';
import {EventType} from '@buncha/config/analytics/type';
import {GroupedAssignedItems} from '@buncha/batchDetails/types';
import {H5Text} from '@buncha/components/core/text';
import {Modules} from '@buncha/utils/types';
import {PTextInput} from '@buncha/components/core/input/TextInput';
import {ShopperScreens} from '@buncha/home/navigation/types';
import {SpinnerComponent} from '@buncha/components/core/loader';
import {Touchable} from '@buncha/components/core/touchable';
import font from '@buncha/appStyles/style/font';
import {getClientType} from '@buncha/common/types/common';
import {useGetLocale} from '@buncha/localization/hooks';

interface Props {
  open: boolean;
  onClose: () => void;
  buyerName: string;
  usedBag: number;
  orderId: number;
  onRefresh?: (bagsUsed?: number) => void;
  item?: GroupedAssignedItems;
  runId?: number;
  isB2BStore: boolean;
}

const UsedBagBottomSheet = (props: Props) => {
  const {open, buyerName, usedBag, orderId, item, runId, isB2BStore} = props;
  const theme = useColorThemeType();
  const {body, button} =
    useGetLocale(Modules.batchDetails, ShopperScreens.BatchDetailsPage) ?? {};
  const [bagCount, setBagCount] = useState<number>(usedBag || 0);

  const {dispatch} = useNavigateVanLoadContext();
  const muliFont = useFontTheme('muli');

  const {addBagsUsersForOrder, loading} = useAddBagsUsedForOrder(dispatch);

  const onPressHandler = useCallback(async () => {
    await addBagsUsersForOrder(orderId, bagCount, runId);
    Analytics.eventWithProps('Confirm bags/boxes used', {
      type: EventType.cta,
      orderId: orderId,
      usedBag: bagCount,
      client: getClientType(isB2BStore),
    });
    props.onClose();
    if (props?.onRefresh) props?.onRefresh(bagCount);
  }, [addBagsUsersForOrder, bagCount, isB2BStore, orderId, props, runId]);

  useEffect(() => {
    if (open)
      Analytics.eventWithProps('Used Bag Bottom Sheet Open', {
        type: EventType.modal,
        orderId: orderId,
        buyerName: buyerName,
        client: getClientType(isB2BStore),
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const bagQuantityUpdate = useCallback((quantity: string) => {
    const qty = Number(quantity);
    if (qty < 100 && qty >= 0) setBagCount(qty);
  }, []);

  const modifyBagQty = useCallback(
    (quantity: number) => {
      const val = quantity ? quantity.toString() : '';
      bagQuantityUpdate(val);
    },
    [bagQuantityUpdate],
  );

  const modifyAddBagQty = useCallback(() => {
    modifyBagQty(bagCount + 1);
  }, [bagCount, modifyBagQty]);

  const modifySubBagQty = useCallback(() => {
    modifyBagQty(bagCount - 1);
  }, [bagCount, modifyBagQty]);

  return (
    <BottomSheet open={open} onClose={usedBag ? props.onClose : undefined}>
      <Box style={BottomSheetStyle.mainContainer}>
        <CustomImage
          style={BottomSheetStyle.imageContainer}
          // resizeMode="cover"
          source={require('@buncha/resource/images/UsedBagImage.png')}
        />
        <H5Text
          style={[BottomSheetStyle.leftAlign, {color: theme?.text.blue[300]}]}>
          {body?.bagsUsed?.[0]}
          <H5Text style={{color: theme?.text.red[100]}}>
            {buyerName || ''}
            {body?.bagsUsed?.[1]}
          </H5Text>
          {body?.bagsUsed?.[2]}
        </H5Text>
        <HBox style={BottomSheetStyle.bagQuantityBox}>
          <CustomerOrderInfo
            groupedAssignedItems={item}
            hideFooter
            buyerName={buyerName}
          />
          <Box
            style={[
              BottomSheetStyle.bagQuantityContainer,
              BottomSheetStyle.bagQuantityPill,
              {
                borderColor: theme?.background.blue[300],
              },
            ]}>
            <HBox style={BottomSheetStyle.bagQuantityContainer}>
              <Touchable
                activeOpacity={1}
                hitSlop={BottomSheetStyle.hitSlopStyles}
                disabled={!bagCount}
                onPress={modifySubBagQty}>
                <MinusIcon
                  size={3}
                  color={
                    !bagCount
                      ? theme?.background.gray[100]
                      : theme?.background.blue[300]
                  }
                />
              </Touchable>
              <PTextInput
                placeholder="0"
                placeholderTextColor={theme?.text.gray[300]}
                debounce={false}
                textAlign="center"
                value={bagCount ? bagCount.toString() : ''}
                onChangeText={bagQuantityUpdate}
                keyboardType="number-pad"
                returnKeyType="done"
                containerStyle={[BottomSheetStyle.inputStyle]}
                style={{
                  fontFamily: muliFont[700].normal,
                  ...font.FontSize.fontH3,
                  color: theme?.text.blue[300],
                }}
              />
              <Touchable
                activeOpacity={1}
                hitSlop={BottomSheetStyle.hitSlopStyles}
                style={BottomSheetStyle.addIconContainer}
                disabled={bagCount >= 99}
                onPress={modifyAddBagQty}>
                <AddIcon size={3} color={theme?.background.blue[300]} />
              </Touchable>
            </HBox>
          </Box>
        </HBox>
        <Box>
          <BottomButton
            title={button?.confirmBags?.replace('{bagCount}', bagCount)}
            disabled={
              loading || usedBag === bagCount || bagCount > 100 || bagCount <= 0
            }
            onPress={onPressHandler}
            isLoading={loading}
            loadingIndicator={<SpinnerComponent />}
            style={[
              BottomSheetStyle.bottomButton,
              {
                backgroundColor: theme?.background.blue[100],
              },
            ]}
          />
        </Box>
      </Box>
    </BottomSheet>
  );
};

export default React.memo(UsedBagBottomSheet);
